import { useEffect, useId, useMemo, useRef } from 'react';
import { useForm, type DefaultValues, type FieldValues, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodTypeAny } from 'zod';
import type { FieldConfig } from './entityFormTypes';
import { formatItDateInputValue } from '@/utils/date';
import { getErrorCount, scrollToFirstError } from './formErrors';
import { toast } from '@/lib/toast';

interface UseEntityDialogFormOptions {
  open: boolean;
  schema: ZodTypeAny;
  fields: readonly FieldConfig[];
  data?: Partial<FieldValues> | null;
  createDefaults?: Partial<FieldValues>;
}

export function useEntityDialogForm({
  open,
  schema,
  fields,
  data,
  createDefaults,
}: UseEntityDialogFormOptions) {
  const formId = useId();
  const isCreateMode = data === null || data === undefined;

  const defaultValues = useMemo(() => {
    if (isCreateMode) {
      const base = fields.reduce(
        (acc, field) => {
          acc[field.key] = field.type === 'checkbox' ? false : '';
          return acc;
        },
        {} as Record<string, string | boolean>
      );

      return { ...base, ...(createDefaults ?? {}) } as Record<string, string | boolean | unknown>;
    }

    return fields.reduce(
      (acc, field) => {
        const value = data?.[field.key];
        if (field.type === 'date') {
          acc[field.key] = formatItDateInputValue(value);
        } else if (field.type === 'checkbox') {
          acc[field.key] = Boolean(value);
        } else if (field.type === 'month-year') {
          acc[field.key] = typeof value === 'string' ? value : '';
        } else if (field.inputFormatter) {
          acc[field.key] = field.inputFormatter(value) || '';
        } else {
          acc[field.key] = value ?? '';
        }
        return acc;
      },
      {} as Record<string, string | boolean>
    );
  }, [fields, data, isCreateMode, createDefaults]);

  const form = useForm<FieldValues>({
    resolver: zodResolver(schema as Parameters<typeof zodResolver>[0]) as Resolver<FieldValues>,
    defaultValues: defaultValues as DefaultValues<FieldValues>,
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const lastDefaultsRef = useRef<Record<string, unknown> | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    const nextDefaults = defaultValues as Record<string, unknown>;
    const prevDefaults = lastDefaultsRef.current;
    const keys = Object.keys(nextDefaults);
    const isSame =
      prevDefaults &&
      keys.length === Object.keys(prevDefaults).length &&
      keys.every((key) => Object.is(prevDefaults[key], nextDefaults[key]));

    if (!wasOpenRef.current || !isSame) {
      lastDefaultsRef.current = nextDefaults;
      form.reset(nextDefaults as DefaultValues<FieldValues>);
    }

    wasOpenRef.current = true;
  }, [open, form, defaultValues]);

  const errorCount = getErrorCount(form.formState.errors);
  const rootError =
    (form.formState.errors as unknown as { root?: { message?: unknown } }).root?.message ?? null;

  const handleInvalidSubmit = () => {
    toast.error('Correggi i campi evidenziati prima di salvare');
    scrollToFirstError(form.formState.errors);
  };

  return {
    form,
    formId,
    defaultValues,
    errorCount,
    rootError: typeof rootError === 'string' ? rootError : null,
    handleInvalidSubmit,
    setRootError: (message: string) => form.setError('root', { type: 'manual', message }),
  };
}
